import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ContentLoader,
  IconCheckmark,
  IconDisabled,
  List,
  Notification,
  StatusCard,
} from 'shared/components';
import { displayValue, formatDate } from 'shared/helpers';
import { fetchDevicesByLicenseUser } from '../../licenses/actions';

const PAGE_SIZE = 10;

const LicenseUserDevices = ({ licenseUserId }) => {
  const { t } = useTranslation();
  const [devices, setDevices] = useState([]);
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [currentSort, setCurrentSort] = useState([
    { desc: true, id: 'created_at' },
  ]);
  const [error, setError] = useState(false);
  const [retryToken, setRetryToken] = useState(0);

  const getDevices = useCallback(
    async (page, sort) => {
      setIsLoading(true);
      try {
        const response = await fetchDevicesByLicenseUser(
          licenseUserId,
          sort,
          page,
          PAGE_SIZE,
        );
        setDevices(get(response, 'data.results', []));
        setCount(get(response, 'data.count', 0));
      } catch (error) {
        Notification(
          'error',
          t('Error occurred'),
          t('There was an error while getting devices'),
        );
        setError(true);
      } finally {
        setIsLoading(false);
      }
    },
    [licenseUserId],
  );

  useEffect(() => {
    if (licenseUserId) {
      getDevices(currentPage, currentSort);
    }
  }, [licenseUserId, currentPage, currentSort, getDevices, retryToken]);

  const handleSortChange = (newSort) => setCurrentSort(newSort);

  const handleRetry = () => {
    setRetryToken((token) => token + 1);
    setError(false);
  };

  const columns = [
    {
      Header: t('Created on'),
      accessor: 'created_at',
      Cell: (cellInfo) => formatDate(cellInfo.value),
      width: 140,
    },
    {
      Header: t('Device name'),
      accessor: 'device_name',
      Cell: (cellData) => displayValue(cellData.value),
      width: 200,
    },
    {
      Header: t('Hardware ID'),
      accessor: 'hardware_id',
      Cell: (cellData) => displayValue(cellData.value),
      width: 220,
    },
    {
      Header: t('License'),
      id: 'license',
      accessor: (row) =>
        get(row, 'license.license_key') ??
        get(row, 'license.licenseKey') ??
        null,
      Cell: (cellData) => displayValue(cellData.value),
      width: 220,
      sortable: false,
    },
    {
      Header: t('Product'),
      accessor: 'license.product.product_name',
      Cell: (cellData) => displayValue(cellData.value),
      sortable: false,
    },
    {
      Header: t('Activated on'),
      accessor: 'activated_at',
      Cell: (cellInfo) => formatDate(cellInfo.value),
      width: 140,
    },
    {
      Header: t('Last seen'),
      accessor: 'last_seen_at',
      Cell: (cellInfo) => formatDate(cellInfo.value),
      width: 140,
    },
    {
      Header: t('Is active'),
      accessor: 'active',
      headerClassName: 'text-center',
      className: 'text-center',
      Cell: (cellData) =>
        cellData.value ? (
          <IconCheckmark color="#10ac84" height="14px" />
        ) : (
          <IconDisabled color="#aaa" height="14px" />
        ),
      width: 90,
      sortable: false,
    },
    {
      Header: t('Status'),
      id: 'status',
      headerClassName: 'text-center',
      className: 'text-center',
      Cell: (rowData) => {
        const isActive = get(rowData, 'original.active');
        const isBlocked = get(rowData, 'original.blocked');

        if (isBlocked) {
          return <StatusCard status="error" text={t('Blocked')} />;
        }

        return (
          <StatusCard
            status={isActive ? 'success' : 'error'}
            text={isActive ? t('Active') : t('Inactive')}
          />
        );
      },
      width: 110,
      sortable: false,
    },
  ];
  if (isLoading) {
    return <ContentLoader text={t('Loading devices')} />;
  }

  if (error) {
    return (
      <div className="LicenseUserDetails-error">
        <p>{t('Unable to load devices for this user')}</p>
        <Button theme="info" size="sm" onClick={handleRetry}>
          {t('Retry')}
        </Button>
      </div>
    );
  }

  if (!devices.length) {
    return (
      <div className="LicenseUserDetails-empty">
        {t('No devices for this user')}
      </div>
    );
  }

  return (
    <div className="LicenseUserDetails-list">
      <List
        data={devices}
        columns={columns}
        minRows={2}
        loading={isLoading}
        manual
        defaultSorted={currentSort}
        onSortedChange={handleSortChange}
        page={currentPage}
        pages={Math.ceil(count / PAGE_SIZE)}
        showPagination={count > PAGE_SIZE}
        onPageChange={(page) => setCurrentPage(page)}
      />
    </div>
  );
};

LicenseUserDevices.propTypes = {
  licenseUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};

export default LicenseUserDevices;
